import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  FormField,
  Input,
  Select,
  SelectProps,
  Alert,
  Spinner,
  Icon,
  Link,
  Checkbox,
} from '@cloudscape-design/components';

import { ViewLayout } from '../../components/ViewLayout';
import { useEffect, useMemo, useState } from 'react';
import ReportCardHeader from '../../components/ReportCardHeader';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoadingOrError } from '../../components/Loading';
import { useNavigate, useParams } from '@tanstack/react-router';
import AutoCompleteSearch from '../../components/AutoCompleteSearch';
import { Suggestion } from '../../common/useMaps';
import { BUSINESS_TYPES } from '../../common/types';
import { getBusinessType } from '../../common/businessTypes';
import { getDistributorByPath, createDistributorReport } from '../../common/api/distributors';
import { formatPhoneNumber } from '../../common/formatting/phoneFormat';
import { PublicScorecardPreview } from '../../components/PublicScorecardPreview';
import {
  PublicScorecardFormHeader,
  PUBLIC_SCORECARD_SUBMIT_LABEL,
} from '../../components/PublicScorecardFormHeader';

const STORAGE_KEY = 'contact_form_data';

type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  title: string;
  referenceName: string;
};

export default function DistributorCreatePage() {
  const navigate = useNavigate();
  const { path } = useParams({ from: '/d/$path' });

  // Fetch the distributor by path
  const {
    data: distributor,
    isLoading: distributorLoading,
    error: distributorError,
  } = useQuery({
    queryKey: ['distributor', path],
    queryFn: () => getDistributorByPath(path!),
    enabled: !!path,
  });

  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);

  const selectedSuggestion = selectedItems.length > 0 ? selectedItems[0] : undefined;

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [textScorecard, setTextScorecard] = useState<boolean>(true);
  const [title, setTitle] = useState<string>('');
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);

  const [showErrors, setShowErrors] = useState<boolean>(false);

  const createMutation = useMutation({
    mutationFn: (params: { path: string; req: Parameters<typeof createDistributorReport>[1] }) =>
      createDistributorReport(params.path, params.req),
    onSuccess: (data) => {
      if (data) {
        navigate({
          to: `/create/${data.public_code}`,
          search: {
            completion: undefined,
            delivery: textScorecard ? (email.trim() ? 'text-email' : 'text') : 'email',
          },
        });
      }
    },
  });

  // Load saved form data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData: ContactFormData = JSON.parse(savedData);
        setFirstName(parsedData.firstName || '');
        setLastName(parsedData.lastName || '');
        setEmail(parsedData.email || '');
        setPhoneNumber(parsedData.phoneNumber || '');
        setTitle(parsedData.title || '');
      } catch (error) {
        console.error('Error parsing saved form data:', error);
      }
    }
  }, []);

  const validPhoneNumber = useMemo(() => {
    if (!phoneNumber.trim()) {
      return false;
    }
    return phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15;
  }, [phoneNumber]);

  const validEmail = useMemo(() => {
    if (!email.trim()) {
      return false;
    }
    return email.trim().includes('@');
  }, [email]);

  const invalidContact = useMemo(() => {
    if (!firstName.trim() || !lastName.trim() || !validEmail || !validPhoneNumber) {
      return true;
    }
    return false;
  }, [firstName, lastName, validEmail, validPhoneNumber]);

  const anyContact = useMemo(() => {
    return !!(
      firstName.trim() ||
      lastName.trim() ||
      email.trim() ||
      phoneNumber.trim() ||
      title.trim()
    );
  }, [firstName, lastName, email, phoneNumber, title]);

  const handleCreate = () => {
    if (createMutation.isPending || !path) {
      return;
    }
    if (!selectedSuggestion) {
      return;
    }
    if (!selectedSuggestion.placeId) {
      return;
    }

    if (!anyContact) {
      setShowErrors(true);
      return;
    }

    if (invalidContact) {
      setShowErrors(true);
      return;
    }

    // Save form data to local storage
    const formData: ContactFormData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim(),
      title: title.trim(),
      referenceName: distributor ? `${distributor.first_name} ${distributor.last_name}` : '',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));

    const contact = {
      first_name: firstName.trim(),
      last_name: lastName.trim() || undefined,
      email: email.trim() || undefined,
      phone_number: textScorecard ? phoneNumber.trim() || undefined : undefined,
      title: title.trim() || undefined,
    };

    createMutation.mutate({
      path,
      req: {
        place_id: selectedSuggestion.placeId,
        contact,
        business_type:
          businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
      },
    });
  };

  useEffect(() => {
    if (selectedItems.length > 0) {
      // try to find the best business type
      const firstItem = selectedItems[0];
      const googleTags = firstItem.tags;
      const foundBusinessType = getBusinessType(googleTags);
      if (foundBusinessType) {
        setBusinessType({ label: foundBusinessType, value: foundBusinessType });
      } else {
        setBusinessType({ label: 'Other', value: 'Other' });
      }
    }
  }, [selectedItems]);

  if (distributorLoading) {
    return (
      <ViewLayout
        header={<ReportCardHeader showDemoCta={false} />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <Container>
          <Box textAlign="center" padding="xxl">
            <Spinner size="large" />
            <Box padding="s">Loading...</Box>
          </Box>
        </Container>
      </ViewLayout>
    );
  }

  if (distributorError || !distributor) {
    return (
      <ViewLayout
        header={<ReportCardHeader showDemoCta={false} />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <Container>
          <Alert type="error" header="Distributor not found">
            The distributor path "{path}" was not found or is no longer active.
          </Alert>
        </Container>
      </ViewLayout>
    );
  }

  const representativePhone = distributor.phone_number || distributor.additional_info;

  return (
    <ViewLayout
      header={<ReportCardHeader distributor={distributor} showDemoCta={false} />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      <SpaceBetween direction="vertical" size="l">
        <Container
          header={<PublicScorecardFormHeader />}
        >
          <SpaceBetween direction="vertical" size="s">
            <SpaceBetween direction="horizontal" size="xxs">
              <Box variant="p" fontSize="heading-s" color="text-status-error">
                *
              </Box>
              <Box variant="p" fontWeight="light" color="text-status-error">
                <i>Required</i>
              </Box>
            </SpaceBetween>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
              ]}
            >
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      First Name
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !firstName.trim()
                    ? 'First name is required'
                    : undefined
                }
              >
                <Input
                  value={firstName}
                  onChange={({ detail }) => setFirstName(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      Last Name
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !lastName.trim() ? 'Last name is required' : undefined
                }
              >
                <Input
                  value={lastName}
                  onChange={({ detail }) => setLastName(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      Email
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !validEmail
                    ? 'Enter a valid email address'
                    : undefined
                }
              >
                <Input
                  value={email}
                  onChange={({ detail }) => setEmail(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="p" fontWeight="bold">
                      Cell Phone Number
                    </Box>
                    <Box variant="p" fontSize="heading-s" color="text-status-error">
                      *
                    </Box>
                  </SpaceBetween>
                }
                errorText={
                  showErrors && anyContact && !validPhoneNumber
                    ? 'Enter a valid phone number'
                    : undefined
                }
              >
                <Input
                  value={phoneNumber}
                  onChange={({ detail }) => setPhoneNumber(detail.value)}
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField label="Scorecard delivery">
                <Checkbox
                  checked={textScorecard}
                  onChange={({ detail }) => setTextScorecard(detail.checked)}
                  disabled={createMutation.isPending}
                >
                  Text me the scorecard
                </Checkbox>
              </FormField>
              <FormField label="Title">
                <Input
                  value={title}
                  onChange={({ detail }) => setTitle(detail.value)}
                  placeholder="(optional)"
                  disabled={createMutation.isPending}
                />
              </FormField>
              <FormField label="Your Representative">
                <SpaceBetween direction="vertical" size="xxs">
                  <Box variant="p" fontWeight="bold">
                    {distributor.first_name} {distributor.last_name}
                  </Box>
                  <SpaceBetween direction="horizontal" size="m">
                    {representativePhone && (
                      <Link
                        href={`tel:${representativePhone.replace(/[^\d+]/g, '')}`}
                        variant="primary"
                      >
                        <SpaceBetween direction="horizontal" size="xxs">
                          <Icon name="call" />
                          <span>{formatPhoneNumber(representativePhone)}</span>
                        </SpaceBetween>
                      </Link>
                    )}
                    {distributor.email && (
                      <Link href={`mailto:${distributor.email}`} variant="primary">
                        <SpaceBetween direction="horizontal" size="xxs">
                          <Icon name="envelope" />
                          <span>{distributor.email}</span>
                        </SpaceBetween>
                      </Link>
                    )}
                  </SpaceBetween>
                </SpaceBetween>
              </FormField>
            </Grid>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xxs">
                  <Box variant="p" fontWeight="bold">
                    Business Location
                  </Box>
                  <Box variant="p" fontSize="heading-s" color="text-status-error">
                    *
                  </Box>
                </SpaceBetween>
              }
              description="Start typing the business name or location and select from the dropdown"
            >
              <AutoCompleteSearch
                selectedItems={selectedItems}
                setSelectedItems={(items: Suggestion[]) =>
                  setSelectedItems(items.length > 0 ? [items[0]] : [])
                }
                loading={createMutation.isPending}
                searchDisabled={selectedItems.length > 0}
                hideSearch={selectedItems.length > 0}
                placeholder="Enter your business name..."
              />
            </FormField>
            <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
              <FormField label="Business Type">
                <Select
                  selectedOption={businessType}
                  options={BUSINESS_TYPES}
                  onChange={({ detail }) => setBusinessType(detail.selectedOption)}
                  disabled={createMutation.isPending}
                  placeholder="Select a business type"
                />
              </FormField>
            </Grid>
            <Box float="right">
              <Button
                variant="primary"
                iconName="add-plus"
                onClick={handleCreate}
                loading={createMutation.isPending}
                disabled={!selectedSuggestion || !anyContact}
              >
                {PUBLIC_SCORECARD_SUBMIT_LABEL}
              </Button>
            </Box>
            {createMutation.isError && (
              <LoadingOrError loading={createMutation.isPending} error={createMutation.error} />
            )}
          </SpaceBetween>
        </Container>
        {!createMutation.isPending && !createMutation.isSuccess && <PublicScorecardPreview />}
      </SpaceBetween>
    </ViewLayout>
  );
}
